import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import InviteModal from '../components/InviteModal';
import { generateRoomId } from '../utils/generateRoomId';
import { useGameStore } from '../stores/gameStore';
import { COPY } from '../copy';

function Home() {
  const navigate = useNavigate();
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showNewGameWarning, setShowNewGameWarning] = useState(false);
  const [currentRoomId, setCurrentRoomId] = useState(null);

  // Check if there's an existing session in localStorage
  const persistedSessionId = useGameStore((state) => state.persistedSessionId);
  const gamePhase = useGameStore((state) => state.gamePhase);

  // Only show rejoin option if there's a persisted session and game isn't ended
  const hasActiveSession = persistedSessionId && gamePhase !== 'ended';

  const handleCreateGame = () => {
    // If there's an active session, warn the user
    if (hasActiveSession) {
      setShowNewGameWarning(true);
      return;
    }

    // Otherwise proceed normally
    startNewGame();
  };

  const startNewGame = () => {
    // Reset game state before creating new game
    // This clears all persisted pieces, attacks, and session data
    console.log('[Home] Resetting game state for new game');
    useGameStore.getState().reset();

    // Generate unique room ID client-side
    const roomId = generateRoomId();
    setCurrentRoomId(roomId);

    // Show invite modal with shareable URL FIRST
    // This allows user to copy the link before navigating
    setShowInviteModal(true);
    setShowNewGameWarning(false);
  };

  const handleRejoinGame = () => {
    if (persistedSessionId) {
      navigate(`/game/${persistedSessionId}`);
    }
  };

  const handleModalClose = () => {
    setShowInviteModal(false);

    // Navigate to game room after modal is closed
    // PartyKit room created automatically on first connection
    if (currentRoomId) {
      navigate(`/game/${currentRoomId}`);
    }
  };

  return (
    <>
      <InviteModal
        isOpen={showInviteModal}
        onClose={handleModalClose}
        roomId={currentRoomId}
      />

      {/* Warning Modal - New Game will erase current session */}
      {showNewGameWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
          <div className="bg-dark-charcoal border-2 border-neon-pink shadow-glow-pink rounded-lg p-6 max-w-md mx-4">
            <h3 className="text-xl font-bold text-neon-pink text-glow-pink mb-4">
              {COPY.home.warningModal.title}
            </h3>
            <p className="text-gray-300 mb-6">
              {COPY.home.warningModal.message}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowNewGameWarning(false)}
                className="flex-1 bg-dark-purple hover:bg-dark-charcoal border border-neon-cyan text-gray-100 font-semibold py-3 px-6 rounded-lg transition-colors"
              >
                {COPY.home.warningModal.cancel}
              </button>
              <button
                onClick={startNewGame}
                className="flex-1 bg-neon-pink hover:bg-neon-purple border-2 border-white/70 text-white font-semibold py-3 px-6 rounded-lg transition-colors shadow-glow-pink"
              >
                {COPY.home.warningModal.confirm}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-2xl mx-auto text-center">
        <div className="bg-dark-charcoal border-2 border-neon-cyan shadow-glow-cyan rounded-lg p-8">
          <h2 className="text-4xl font-bold text-neon-cyan text-glow-cyan mb-4">
            {COPY.home.welcome.title}
          </h2>
          <p className="text-gray-300 mb-8">
            {COPY.home.welcome.subtitle}
          </p>

          {/* Active Session Banner */}
          {hasActiveSession && (
            <div className="mb-6 bg-dark-purple border-2 border-neon-purple shadow-glow-purple rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="text-left">
                  <p className="font-semibold text-neon-purple text-glow-purple">
                    Active Game Found
                  </p>
                  <p className="text-sm text-gray-300">
                    Session: {persistedSessionId.substring(0, 8)}...
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    Phase: {gamePhase}
                  </p>
                </div>
                <button
                  onClick={handleRejoinGame}
                  className="bg-neon-purple hover:bg-neon-pink border-2 border-white/70 text-white font-semibold py-2 px-6 rounded-lg transition-colors shadow-glow-purple"
                >
                  {COPY.home.buttons.rejoinGame}
                </button>
              </div>
            </div>
          )}

          <div className="space-y-4">
            <button
              onClick={handleCreateGame}
              className="w-full bg-neon-cyan hover:bg-neon-pink border-2 border-white/70 text-dark-navy hover:text-white font-bold py-4 px-8 rounded-lg transition-all text-lg shadow-glow-cyan"
            >
              {hasActiveSession ? COPY.home.buttons.startNewGame : COPY.home.buttons.inviteOpponent}
            </button>

            <div className="text-sm text-gray-400">
              <p>
                {hasActiveSession
                  ? 'Warning: Starting a new game will erase your current session'
                  : 'Click to create a game and get a shareable link'}
              </p>
            </div>
          </div>

          <div className="mt-12 text-left">
            <h3 className="text-xl font-semibold text-neon-pink text-glow-pink mb-4">
              {COPY.home.howToPlay.title}
            </h3>
            <ol className="list-decimal list-inside space-y-2 text-gray-300">
              {COPY.home.howToPlay.steps.map((step) => (
                <li key={step.number}>
                  <strong>{step.title}:</strong> {step.description}
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>
    </>
  );
}

export default Home;
